import React, { useState } from 'react';
import type { RouteComponentProps } from '@reach/router';
import {
  ApolloClient,
  useApolloClient,
  useMutation,
  useQuery,
} from '@apollo/client';

import { Container, TextField, Grid } from '@mui/material';
import { makeStyles } from '@mui/styles';
import MaterialTable from 'material-table';
import { DeleteOutline, EditOutlined, Add, GetApp, PictureAsPdf } from '@mui/icons-material';

import DateFnsAdapter from '@date-io/date-fns';

import { DatePicker } from '@mui/x-date-pickers';

import { Loading } from '../components/Loading';
import { MainContainer } from '../components/MainContainer';
import { Header } from '../components/Header';
import { FloatingButton } from '../components/FloatingButton';
import { AddAdoptionDialog } from '../components/AddAdoptionDialog';
import { EditAdoptionDialog } from '../components/EditAdoptionDialog';
import { AreYouSureDialog } from '../components/AreYouSureDialog';

import { PatchedPagination } from '../utils/PatchedPagination';

import type { 
  // Options as MTableOptions, 
  // Column 
} from 'material-table';

// import { GET_ADOPTIONS } from '../gql/queries/getAdoptions';
import { GET_ADOPTIONS } from '../gql/queries/getAdoptions';
import { ANY_ALERT_AVAILABLE } from '../gql/queries/anyAlertAvailable';
import { CREATE_ADOPTION } from '../gql/mutations/CreateAdoption';
import { UPDATE_ADOPTION } from '../gql/mutations/UpdateAdoption';
import { DELETE_ADOPTION } from '../gql/mutations/DeleteAdoption';

import jsPDF from 'jspdf';
import 'jspdf-autotable';

interface AdoptionsProps extends RouteComponentProps {
  me?: any;
}

const useStyles = makeStyles((theme) => ({
  cardGrid: {
    backgroundColor: theme.palette.background.paper,
    paddingTop: theme.spacing(0),
    paddingBottom: theme.spacing(8),
  },
}));

const dateFns = new DateFnsAdapter();

const getColumns: any = ({ me }: any) => [
  {
    title: 'Nr. Crt.',
    field: 'regNumber',
  },
  {
    title: 'Specie',
    field: 'animalSpecies',
    render: ({ animalSpecies }: any) => {
      switch (animalSpecies) {
        case 'dog':
          return 'caine';
        case 'cat':
          return 'pisica';
        case 'other':
          return 'alta specie';
      }
    },
    lookup: {
      dog: 'caine',
      cat: 'pisica',
      other: 'alta specie',
    },
  },
  {
    title: 'Sex',
    field: 'animalSex',
    render: ({ animalSex }: any) => {
      switch (animalSex) {
        case 'male':
          return 'mascul';
        case 'female':
          return 'femela';
      }
    },
    lookup: {
      male: 'mascul',
      female: 'femela',
    },
  },
  {
    title: 'Nume',
    field: 'animalName',
  },
  {
    title: 'Microcip',
    field: 'animalMicrochipStatus',
    render: ({ animalMicrochipStatus }: any) => {
      switch (animalMicrochipStatus) {
        case 'eduxanima':
          return 'Eduxanima';
        case 'other':
          return 'Alt cabinet';
        case 'no':
          return 'Nu';
      }
    },
    lookup: {
      eduxanima: 'Eduxanima',
      other: 'Alt cabinet',
      no: 'Nu',
    },
  },
  {
    title: 'Nr. microcip',
    field: 'animalMicrochipNumber',
  },
  {
    title: 'De unde',
    field: 'animalLocation',
  },
  {
    title: 'Data intrare',
    field: 'animalEnteredDate',
    render: ({ animalEnteredDate }: any) => {
      if (!animalEnteredDate) return '';
      try {
        const newDate = new Date(animalEnteredDate);
        if (isNaN(newDate.getTime())) return ''; // Check if date is valid
        return dateFns.formatByString(newDate, 'dd/MM/yyyy');
      } catch (error) {
        return '';
      }
    },
  },
  {
    title: 'Data iesire',
    field: 'animalLeaveDate',
    render: ({ animalLeaveDate }: any) => {
      if (!animalLeaveDate) return '';
      try {
        const newDate = new Date(animalLeaveDate);
        if (isNaN(newDate.getTime())) return ''; // Check if date is valid
        return dateFns.formatByString(newDate, 'dd/MM/yyyy');
      } catch (error) {
        return '';
      }
    },
  },
  // {
  //   title: 'Fişă creată de',
  //   field: 'createdBy.email',
  // },
  // {
  //   title: 'La data de',
  //   field: 'createdAt',
  //   render: ({ createdAt }: any) => {
  //     const newDate = new Date(createdAt);
  //     return dateFns.formatByString(newDate, 'HH:mm dd/MM/yyyy');
  //   },
  // },
  // {
  //   title: 'Modificat de',
  //   field: 'updatedBy.email',
  // },
  // {
  //   title: 'La data de',
  //   field: 'lastUpdate',
  //   render: ({ lastUpdate }: any) => {
  //     const newDate = new Date(lastUpdate);
  //     return dateFns.formatByString(newDate, 'HH:mm dd/MM/yyyy');
  //   },
  // },
];

const getColumnsAnimalsAdopted: any = ({ me }: any) => [
  {
    title: 'Nr. Crt.',
    field: 'regNumber',
  },
  {
    title: 'Specie',
    field: 'animalSpecies',
    render: ({ animalSpecies }: any) => {
      switch (animalSpecies) {
        case 'dog':
          return 'caine';
        case 'cat':
          return 'pisica';
        case 'other':
          return 'alta specie';
      }
    },
    lookup: {
      dog: 'caine',
      cat: 'pisica',
      other: 'alta specie',
    },
  },
  {
    title: 'Nume',
    field: 'animalName',
  },
  {
    title: 'Microcip',
    field: 'animalMicrochipStatus',
    render: ({ animalMicrochipStatus }: any) => {
      switch (animalMicrochipStatus) {
        case 'eduxanima':
          return 'Eduxanima';
        case 'other':
          return 'Alt cabinet';
        case 'no':
          return 'Nu';
      }
    },
    lookup: {
      eduxanima: 'Eduxanima',
      other: 'Alt cabinet',
      no: 'Nu',
    },
  },
  {
    title: 'Nr. microcip',
    field: 'animalMicrochipNumber',
  },
  {
    title: 'Data iesire',
    field: 'animalLeaveDate',
    render: ({ animalLeaveDate }: any) => {
      if (!animalLeaveDate) return '';
      try {
        const newDate = new Date(animalLeaveDate);
        if (isNaN(newDate.getTime())) return ''; // Check if date is valid
        return dateFns.formatByString(newDate, 'dd/MM/yyyy');
      } catch (error) {
        return '';
      }
    },
  },
  {
    title: 'Sex',
    field: 'animalSex',
    render: ({ animalSex }: any) => {
      switch (animalSex) {
        case 'male':
          return 'mascul';
        case 'female':
          return 'femela';
      }
    },
    lookup: {
      male: 'mascul',
      female: 'femela',
    },
  },
  {
    title: 'Fisa creata de',
    field: 'createdBy.email',
  },
  {
    title: 'La data de',
    field: 'createdAt',
    render: ({ createdAt }: any) => {
      if (!createdAt) return '';
      try {
        const newDate = new Date(createdAt);
        if (isNaN(newDate.getTime())) return ''; // Check if date is valid
        return dateFns.formatByString(newDate, 'HH:mm dd/MM/yyyy');
      } catch (error) {
        return '';
      }
    },
  },
  {
    title: 'Modificat de',
    field: 'updatedBy.email',
  },
  {
    title: 'La data de',
    field: 'lastUpdate',
    render: ({ lastUpdate }: any) => {
      if (!lastUpdate) return '';
      try {
        const newDate = new Date(lastUpdate);
        if (isNaN(newDate.getTime())) return ''; // Check if date is valid
        return dateFns.formatByString(newDate, 'HH:mm dd/MM/yyyy');
      } catch (error) {
        return '';
      }
    },
  },
];

// interface CustomExportButton {
//   csv?: boolean;
//   pdf?: boolean;
//   customExport?: (columns: Column<any>[], data: any[]) => {
//     text: string;
//     onClick: () => void;
//   }[];
// }

// interface ExtendedOptions extends MTableOptions<any> {
//   exportButton?: boolean | CustomExportButton;
// }

export const Adoptions: React.FC<AdoptionsProps> = ({ me }) => {
  const client: ApolloClient<any> = useApolloClient();
  const classes = useStyles();
  const today = new Date();
  const dateFns = new DateFnsAdapter();
  const newDate = dateFns.formatByString(today, 'yyyy-MM-dd');
  const initialDate = new Date('2021-01-01');
  const monthBeginning = dateFns.formatByString(today, 'yyyy-MM-01');

  const [openAddDialog, setOpenAddDialog] = useState<boolean>(false);
  const [openEditDialog, setOpenEditDialog] = useState<boolean>(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState<boolean>(false);
  const [selectedAdoption, setSelectedAdoption] = useState<any>();
  const [startDate, setStartDate] = useState<any>(monthBeginning);
  const [endDate, setEndDate] = useState<any>(newDate);

  const title = 'Eşti sigur că vrei să ştergi această înregistrare?';
  const content =
    'Ştergerea este definitivă şi această înregistrare nu mai poate fi adusă înapoi.';

  const yes = 'Şterge';
  const no = 'Închide';

  const { data, loading, error } = useQuery(GET_ADOPTIONS, {
    variables: {
      startDate,
      endDate,
    },
  });

  // Filter data based on status and create new arrays
  const animalsEntered = data?.getAdoptions
    ? data.getAdoptions
        .filter((animal: any) => animal.animalStatus.entered)
        .map((animal: any) => ({ ...animal }))
    : [];
  const animalsAdopted = data?.getAdoptions
    ? data.getAdoptions
        .filter((animal: any) => animal.animalStatus.adopted)
        .map((animal: any) => ({ ...animal }))
    : [];
  const animalsIntermediated = data?.getAdoptions
    ? data.getAdoptions
        .filter((animal: any) => animal.animalStatus.intermediated)
        .map((animal: any) => ({ ...animal }))
    : [];

  function onStartDateChange(value: any) {
    const newValue = dateFns.formatByString(value, 'yyyy-MM-dd');
    setStartDate(newValue);
  }

  function onEndDateChange(value: any) {
    const newValue = dateFns.formatByString(value, 'yyyy-MM-dd');
    setEndDate(newValue);
  }

  function openAdd() {
    setOpenAddDialog(true);
  }

  function onCloseAddDialog() {
    setOpenAddDialog(false);
  }

  function onCloseEditDialog() {
    setOpenEditDialog(false);
  }

  function onCloseDeleteDialog() {
    setOpenDeleteDialog(false);
  }

  function removeAdoptionDialog(event: any, rowData: any) {
    event.preventDefault();

    setSelectedAdoption(rowData);
    setOpenDeleteDialog(true);
  }

  function editAdoptionDialog(event: any, rowData: any) {
    event.preventDefault();

    setSelectedAdoption(rowData);
    setOpenEditDialog(true);
  }

  const [
    createAdoption,
    { loading: createAdoptionLoading, error: createAdoptionError },
  ] = useMutation(CREATE_ADOPTION, {
    refetchQueries: [
      {
        query: GET_ADOPTIONS,
        variables: {
          startDate,
          endDate,
        },
      },
    ],
    awaitRefetchQueries: true,
  });

  const [
    updateAdoption,
    { loading: updateAdoptionLoading, error: updateAdoptionError },
  ] = useMutation(UPDATE_ADOPTION, {
    refetchQueries: [
      {
        query: GET_ADOPTIONS,
        variables: {
          startDate,
          endDate,
        },
      },
    ],
    awaitRefetchQueries: true,
  });

  const [
    deleteAdoption,
    { loading: deleteAdoptionLoading, error: deleteAdoptionError },
  ] = useMutation(DELETE_ADOPTION, {
    refetchQueries: [
      {
        query: GET_ADOPTIONS,
        variables: {
          startDate,
          endDate,
        },
      },
    ],
    awaitRefetchQueries: true,
  });

  async function onRegisterAdoption(
    event: React.FormEvent<HTMLFormElement>,
    variables: any
  ) {
    event.preventDefault();

    try {
      if (!variables) throw new Error('Nu există variabile.');
      if (!variables.regNumber)
        throw new Error('Nr. înregistrare nu este completat');
      if (!variables.animalEnteredDate)
        throw new Error('Data de intrare nu este completată.');
      if (!variables.animalLocation)
        throw new Error('Locaţia nu este completată.');
      if (!variables.animalSpecies)
        throw new Error('Specia animalului nu este completată.');
      if (!variables.animalSex)
        throw new Error('Sexul animalului nu este completat.');
      if (typeof variables.animalMicrochipStatus === 'undefined')
        throw new Error('Microchip nu este completat.');
      if (
        variables.animalMicrochipNumber &&
        (variables.animalMicrochipNumber.length < 15 ||
          variables.animalMicrochipNumber.length > 15)
      ) {
        throw new Error('Numărul microcipului trebuie să aibă 15 caractere.');
      }
      if (
        (variables.animalStatus.adopted ||
          variables.animalStatus.intermediated) &&
        !variables.animalLeaveDate
      )
        throw new Error('Data de ieşire nu este completată');

      await createAdoption({
        variables,
      });

      return onCloseAddDialog();
    } catch (err: any) {
      client.writeQuery({
        query: ANY_ALERT_AVAILABLE,
        data: {
          alertOpen: true,
          alertSeverity: 'error',
          alertMessage: `${err.message}`,
        },
      });
    }
  }

  async function onEditAdoption(
    event: React.FormEvent<HTMLFormElement>,
    input: any
  ) {
    event.preventDefault();

    try {
      if (!input) throw new Error('Nu există variabile.');
      if (!input.regNumber)
        throw new Error('Nr. înregistrare nu este completat');
      if (!input.animalEnteredDate)
        throw new Error('Data de intrare nu este completată.');
      if (!input.animalLocation) throw new Error('Locaţia nu este completată.');
      if (!input.animalSpecies)
        throw new Error('Specia animalului nu este completată.');
      if (!input.animalSex)
        throw new Error('Sexul animalului nu este completat.');
      if (typeof input.animalMicrochipStatus === 'undefined')
        throw new Error('Microchip nu este completat.');
      if (
        input.animalMicrochipNumber &&
        (input.animalMicrochipNumber.length < 15 ||
          input.animalMicrochipNumber.length > 15)
      ) {
        throw new Error('Numărul microcipului trebuie să aibă 15 caractere.');
      }
      if (
        (input.animalStatus.adopted || input.animalStatus.intermediated) &&
        !input.animalLeaveDate
      )
        throw new Error('Data de ieşire nu este completată');

      const variables: any = { id: selectedAdoption?.id, ...input };
      await updateAdoption({ variables });

      return onCloseEditDialog();
    } catch (err: any) {
      client.writeQuery({
        query: ANY_ALERT_AVAILABLE,
        data: {
          alertOpen: true,
          alertSeverity: 'error',
          alertMessage: `${err.message}`,
        },
      });
    }
  }

  async function removeAdoption() {
    const variables: any = { id: selectedAdoption?.id };
    await deleteAdoption({ variables });
    setOpenDeleteDialog(false);
  }

  const exportAllData = (getData: any, title: string) => {
    const allData = getData();
    
    const headers = getColumns({ me })
      .map((col: any) => col.title)
      .join(',');
    
    const rows = allData.map((row: any) => 
      getColumns({ me })
        .map((col: any) => {
          const value = col.render ? col.render(row) : row[col.field];
          return `"${value || ''}"`;
        })
        .join(',')
    );
    
    const csv = [headers, ...rows].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${title}_complete.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportAllToPdf = (getData: any, title: string) => {
    const allData = getData();
    const columns = getColumns({ me });
    
    const doc = new jsPDF('l', 'mm', 'a4');
    
    // Add title
    doc.setFontSize(16);
    doc.text(title, 14, 10);
    
    // Prepare data for autotable
    const headers = columns.map((col: any) => col.title);
    const data = allData.map((row: any) =>
      columns.map((col: any) => {
        return col.render ? col.render(row) : row[col.field] || '';
      })
    );

    // Add table
    doc.autoTable({
      head: [headers],
      body: data,
      startY: 20,
      styles: { fontSize: 8, cellPadding: 1 },
      headStyles: { fillColor: [66, 66, 66] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
      margin: { top: 20 },
    });

    // Save PDF
    doc.save(`${title}_complete.pdf`);
  };

  if (
    loading ||
    createAdoptionLoading ||
    updateAdoptionLoading ||
    deleteAdoptionLoading
  )
    return <Loading />;
  if (
    error ||
    createAdoptionError ||
    updateAdoptionError ||
    deleteAdoptionError
  ) {
    return <p>Error</p>;
  }

  return (
    <MainContainer maxWidth="xl">
      <Header me={me} />
      <Container className={classes.cardGrid} maxWidth="xl">
        <Container maxWidth="sm">
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <DatePicker
                label="Începând cu"
                value={startDate}
                onChange={onStartDateChange}
                inputFormat={'dd/MM/yyyy'}
                renderInput={(params) => <TextField {...params} />}
                minDate={initialDate}
                maxDate={today}
              />
            </Grid>
            <Grid item xs={6}>
              <DatePicker
                label="Până la"
                value={endDate}
                onChange={onEndDateChange}
                inputFormat={'dd/MM/yyyy'}
                renderInput={(params) => <TextField {...params} />}
                minDate={initialDate}
                maxDate={today}
              />
            </Grid>
          </Grid>
        </Container>

        <br />

        <MaterialTable
          title="Animale intrate"
          columns={getColumns({ me })}
          data={animalsEntered}
          components={{
            Pagination: PatchedPagination,
          }}
          options={{
            exportButton: true,
            filtering: true,
          }}
          actions={[
            {
              icon: () => <GetApp />,
              tooltip: 'Exportă CSV (toate datele)',
              isFreeAction: true,
              onClick: () => exportAllData(() => animalsEntered, 'Animale_intrate')
            },
            {
              icon: () => <PictureAsPdf />,
              tooltip: 'Exportă PDF (toate datele)',
              isFreeAction: true,
              onClick: () => exportAllToPdf(() => animalsEntered, 'Animale_intrate')
            },
            {
              icon: DeleteOutline,
              tooltip: 'Şterge înregistrarea',
              onClick: (event, rowData) => removeAdoptionDialog(event, rowData),
              disabled: me.type === 'VIEWER',
            },
            {
              icon: EditOutlined,
              tooltip: 'Editează înregistrarea',
              onClick: (event, rowData) => editAdoptionDialog(event, rowData),
              disabled: me.type === 'VIEWER',
            },
          ]}
          localization={{
            toolbar: {
              searchPlaceholder: 'Caută',
              searchTooltip: 'Caută',
              exportTitle: 'Exportă',
              exportAriaLabel: 'Exportă',
              exportCSVName: 'Exportă CSV (pagina curentă)',
              exportPDFName: 'Exportă PDF (pagina curentă)',
            },
            pagination: {
              previousTooltip: 'Pagina precedentă',
              nextTooltip: 'Pagina următoare',
              firstTooltip: 'Prima pagină',
              lastTooltip: 'Ultima pagină',
              labelRowsSelect: 'Rânduri',
            },
            header: {
              actions: 'Acţiuni',
            },
          }}
        />
        <br />

        <MaterialTable
          title="Animale adoptate"
          columns={getColumnsAnimalsAdopted({ me })}
          data={animalsAdopted}
          components={{
            Pagination: PatchedPagination,
          }}
          options={{
            exportButton: true,
            filtering: true,
          }}
          actions={[
            {
              icon: () => <GetApp />,
              tooltip: 'Exportă CSV (toate datele)',
              isFreeAction: true,
              onClick: () => exportAllData(() => animalsAdopted, 'Animale_adoptate')
            },
            {
              icon: () => <PictureAsPdf />,
              tooltip: 'Exportă PDF (toate datele)',
              isFreeAction: true,
              onClick: () => exportAllToPdf(() => animalsAdopted, 'Animale_adoptate')
            },
            {
              icon: DeleteOutline,
              tooltip: 'Şterge înregistrarea',
              onClick: (event, rowData) => removeAdoptionDialog(event, rowData),
              disabled: me.type === 'VIEWER',
            },
            {
              icon: EditOutlined,
              tooltip: 'Editează înregistrarea',
              onClick: (event, rowData) => editAdoptionDialog(event, rowData),
              disabled: me.type === 'VIEWER',
            },
          ]}
          localization={{
            toolbar: {
              searchPlaceholder: 'Caută',
              searchTooltip: 'Caută',
              exportTitle: 'Exportă',
              exportAriaLabel: 'Exportă',
              exportCSVName: 'Exportă CSV (pagina curentă)',
              exportPDFName: 'Exportă PDF (pagina curentă)',
            },
            pagination: {
              previousTooltip: 'Pagina precedentă',
              nextTooltip: 'Pagina următoare',
              firstTooltip: 'Prima pagină',
              lastTooltip: 'Ultima pagină',
              labelRowsSelect: 'Rânduri',
            },
            header: {
              actions: 'Acţiuni',
            },
          }}
        />

        <br />

        <MaterialTable
          title="Animale intermediate"
          columns={getColumns({ me })}
          data={animalsIntermediated}
          components={{
            Pagination: PatchedPagination,
          }}
          options={{
            exportButton: true,
            filtering: true,
          }}
          actions={[
            {
              icon: () => <GetApp />,
              tooltip: 'Exportă CSV (toate datele)',
              isFreeAction: true,
              onClick: () => exportAllData(() => animalsIntermediated, 'Animale_intermediare')
            },
            {
              icon: () => <PictureAsPdf />,
              tooltip: 'Exportă PDF (toate datele)',
              isFreeAction: true,
              onClick: () => exportAllToPdf(() => animalsIntermediated, 'Animale_intermediare')
            },
            {
              icon: DeleteOutline,
              tooltip: 'Şterge înregistrarea',
              onClick: (event, rowData) => removeAdoptionDialog(event, rowData),
              disabled: me.type === 'VIEWER',
            },
            {
              icon: EditOutlined,
              tooltip: 'Editează înregistrarea',
              onClick: (event, rowData) => editAdoptionDialog(event, rowData),
              disabled: me.type === 'VIEWER',
            },
          ]}
          localization={{
            toolbar: {
              searchPlaceholder: 'Caută',
              searchTooltip: 'Caută',
              exportTitle: 'Exportă',
              exportAriaLabel: 'Exportă',
              exportCSVName: 'Exportă CSV (pagina curentă)',
              exportPDFName: 'Exportă PDF (pagina curentă)',
            },
            pagination: {
              previousTooltip: 'Pagina precedentă',
              nextTooltip: 'Pagina următoare',
              firstTooltip: 'Prima pagină',
              lastTooltip: 'Ultima pagină',
              labelRowsSelect: 'Rânduri',
            },
            header: {
              actions: 'Acţiuni',
            },
          }}
        />
      </Container>
      {me.type !== 'VIEWER' && (
        <FloatingButton icon={<Add />} onClick={() => openAdd()} />
      )}
      <AddAdoptionDialog
        title={'Fişă animal'}
        open={openAddDialog}
        onClose={onCloseAddDialog}
        onSave={onRegisterAdoption}
      />
      <EditAdoptionDialog
        title={'Editează fişă animal'}
        open={openEditDialog}
        onClose={onCloseEditDialog}
        onSave={onEditAdoption}
        animal={selectedAdoption}
        startDate={startDate}
        endDate={endDate}
      />
      <AreYouSureDialog
        title={title}
        content={content}
        yes={yes}
        no={no}
        open={openDeleteDialog}
        onClose={onCloseDeleteDialog}
        onYes={removeAdoption}
      />
    </MainContainer>
  );
};
